import {
  createStyles,
  Grid,
  withStyles,
  Alert,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormSectionTitle,
  InputField,
  Tooltip,
  Typography,
  DialogContent,
  CalculatorIcon,
  BasketIcon,
  DialogToggles,
  DialogActions,
  Button,
  DialogActionArrows,
  useIsMobile
} from '@paintscout/ui'
import type { MuiTheme as Theme, WithStyles } from '@paintscout/ui'
import {
  CoatsSelector,
  RateTypeSelect,
  KeypadInputField,
  OverridableInputField,
  Editor,
  FormSection,
  withDialogStackContext
} from '@paintscout/ui-business'
import type { WithDialogStackContext } from '@paintscout/ui-business'
import type {
  OverridableValue,
  UnitAbbreviation,
  UpdateableAreaSubstrate,
  UpdateableProduct
} from '@paintscout/util/builder'
import {
  updateAreaSubstrate,
  createAreaSubstrate,
  getArea,
  getAreaSubstrate,
  getFeature,
  getObjectLabels
} from '@paintscout/util/builder'
import { isLooselyEqual, makeHtml } from '@paintscout/util/util'
import classnames from 'classnames'
import set from 'lodash/set'
import type { OptionsDocument, QuoteDocument } from 'paintscout'
import React, { useEffect, useState, useRef } from 'react'
import DescriptionIcon from '@material-ui/icons/Description'
import SettingsIcon from '@material-ui/icons/Settings'
import CalculationHelper from '../../EditAreaDialog/CalculationHelper'
import { useQuote } from '../../../context'
import AreaSubstrateProductForm from './AreaSubstrateProductForm'
import DimensionOverrides from './DimensionOverrides'

export interface EditRateFormProps extends WithStyles<typeof styles>, WithDialogStackContext {
  options: OptionsDocument
  quote: QuoteDocument
  areaKey: string
  disabled?: boolean
  editingKey: string

  onChange?: (areaKey: string, areaSubstrate: UpdateableAreaSubstrate) => void
  onConfirm: (quote: QuoteDocument, itemKey?: string) => any
  onToggleEditLabel?: (event: any) => void
  onCancel: () => any
  handleNavArrowClick: (itemKey: string) => void
  nextItemKey?: string
  previousItemKey?: string
  checkParentHasChanges?: () => boolean
}

export const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    content: {},
    title: {
      marginBottom: theme.spacing()
    },
    formRow: {
      marginBottom: theme.spacing()
    },
    dimensionOverrides: {
      [theme.breakpoints.down('xs')]: {
        justifyContent: 'center'
      }
    },
    formSection: {
      padding: `${theme.spacing(2)}px 0 ${theme.spacing(2)}px`
    },
    calculationHelper: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(6),
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing()
      }
    },
    spacer: {},

    badgeRoot: {
      marginBottom: '5px'
    },
    editorLabel: {
      display: 'block',
      color: theme.paintscout.palette.brand.Neutral.P40,
      fontWeight: 500,
      fontSize: '12px',
      whiteSpace: 'nowrap',
      marginRight: theme.spacing(1.5),
      '& span': {
        color: theme.palette.error.main,
        position: 'absolute',
        top: 2.5
      }
    },
    tooltipIcon: {
      marginLeft: theme.spacing(0)
    },
    tooltipBadge: {
      right: theme.spacing(0)
    },
    backButton: {
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          backgroundColor: 'transparent !important',
          color: `${theme.palette.primary.main} !important`
        }
      }
    }
  })
}

function EditRateForm(props: EditRateFormProps) {
  const {
    classes,
    quote,
    options,
    areaKey,
    editingKey,
    onConfirm,
    onCancel,
    handleNavArrowClick,
    nextItemKey,
    previousItemKey,
    disabled
  } = props

  const tooltipClasses = { icon: classes.tooltipIcon, badge: classes.tooltipBadge }

  const areaViewDescriptionRef = useRef(null)
  const substrateViewDescriptionRef = useRef(null)
  const crewNoteRef = useRef(null)
  const dirtyAreaDescription = useRef(false)
  const dirtySubstrateDescription = useRef(false)
  const dirtyCrewNote = useRef(false)

  const clientLabelRef = useRef(null)
  const internalLabelRef = useRef(null)

  const area = getArea({ quote, options, key: areaKey })
  const objectLabels = getObjectLabels({ options })

  const mobile = useIsMobile()
  const { isNoteEditable } = useQuote()
  const features = getFeature({ options, path: 'quotes.areaSubstrates' })

  const advancedOptionsFeature = getFeature({ options, path: 'quotes.areaSubstrates.advancedOptions' })
  const multiProductRatesFeature = getFeature({ options, path: 'quotes.areaSubstrates.multiProductRates' })
  const areaSubstrate = useRef<UpdateableAreaSubstrate>(null)

  if (editingKey) {
    areaSubstrate.current = getAreaSubstrate({ quote, options, areaKey, key: editingKey }) as UpdateableAreaSubstrate
  } else {
    areaSubstrate.current = createAreaSubstrate({ quote, options })
  }

  const areaSubstrateKey = areaSubstrate.current?.key

  const rate = areaSubstrate.current?.rate
  const useCoats = rate?.useCoats
  const rateLabel = areaSubstrate.current
    ? getRateString(areaSubstrate.current, objectLabels.currency.symbol, objectLabels.unit.abbreviation)
    : ''

  const [showFormula, setShowFormula] = useState(!!JSON.parse(window.localStorage.getItem('showAreaSubstrateFormula')))

  const [substrateLabels, setSubstrateLabels] = useState({
    clientLabel: areaSubstrate.current?.clientLabel ?? '',
    name: areaSubstrate.current?.name ?? ''
  })

  useEffect(() => {
    if (areaSubstrate.current) {
      setSubstrateLabels({
        clientLabel: areaSubstrate.current.clientLabel ?? '',
        name: areaSubstrate.current.name ?? ''
      })
      clientLabelRef.current.value = areaSubstrate.current.clientLabel ?? ''
      internalLabelRef.current.value = areaSubstrate.current.name ?? ''
    }
  }, [areaSubstrate.current?.clientLabel, areaSubstrate.current?.name])

  const hidePrep = rate.calculationType === 'price'
  const hidePrice = rate.calculationType === 'price'

  const initialCrewNote = makeHtml(areaSubstrate.current.notes)

  const toggles = [
    { key: 'pricing', label: 'Hours/Cost', value: true, icon: CalculatorIcon },
    { key: 'materials', label: 'Materials', value: true, icon: BasketIcon },
    { key: 'notes', label: 'Notes', value: true, icon: DescriptionIcon }
  ]
  if (advancedOptionsFeature.enabled) {
    toggles.push({ key: 'advanced', label: 'Advanced', value: true, icon: SettingsIcon })
  }

  if (!areaSubstrate.current) {
    return null
  }

  const hasLocalChanges = () => {
    const updatedAreaSubstrate = syncedAreaSubstrate()
    const currentAreaSubstrate = getCurrentAreaSubstrate()
    return !isLooselyEqual(currentAreaSubstrate, updatedAreaSubstrate)
  }
  const checkParentHasChanges = () => props.checkParentHasChanges?.() || hasLocalChanges()

  return (
    <>
      <DialogContent className={classes.content}>
        <form
          id="edit-rate-form"
          onKeyDown={(keyEvent) => {
            if ((keyEvent.charCode || keyEvent.keyCode) === 13 && !(keyEvent.target as any).editor) {
              onComplete()
            }
          }}
        >
          <div
            className={classnames(
              classes.root,
              `calculation-type-${rate.calculationType}`,
              `rate-type-${rate.rateType}`
            )}
          >
            <FormSection
              hideDivider
              classes={{ root: classnames({ [classes.formSection]: true, 'rate-names': true }) }}
              style={{ paddingTop: 0 }}
            >
              {areaSubstrate.current?.inSubstrateGroup && (
                <Alert severity="warning">
                  This {objectLabels.substrate.value} is part of a {objectLabels.substrate.value.toLowerCase()}-view
                  group, so it is not included in the total for this area.
                </Alert>
              )}
              <Grid item xs={12} className={classes.title}>
                <Typography variant={'h4'}>Rate</Typography>
              </Grid>
              <Grid container spacing={mobile ? 2 : 3} justifyContent="center" alignItems="flex-end">
                <Grid item xs={12} md={6}>
                  <InputField
                    label={'Internal Label'}
                    name={'name'}
                    className={'work-order-name'}
                    fullWidth
                    defaultValue={substrateLabels.name}
                    inputRef={internalLabelRef}
                    disabled={disabled}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InputField
                    label={'Client Label'}
                    name={'clientLabel'}
                    className={'quote-name'}
                    fullWidth
                    defaultValue={substrateLabels.clientLabel}
                    inputRef={clientLabelRef}
                    disabled={disabled}
                  />
                </Grid>
              </Grid>
            </FormSection>
            {rate && rate.custom && (
              <FormSection hideDivider>
                <Grid container spacing={mobile ? 2 : 3} classes={{ container: classes.formRow }}>
                  <Grid item xs={12}>
                    <RateTypeSelect
                      rateType={rate.rateType}
                      calculationType={rate.calculationType}
                      onChange={handleRateTypeChange}
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              </FormSection>
            )}

            {rate.rateType !== 'quantity' && rate.rateType !== 'none' && (
              <FormSection hideDivider>
                <Grid
                  container
                  spacing={mobile ? 2 : 3}
                  classes={{ container: classnames(classes.formRow, classes.dimensionOverrides) }}
                >
                  <DimensionOverrides
                    quote={quote}
                    rate={rate}
                    area={area}
                    areaSubstrate={areaSubstrate.current}
                    onChange={handleOverrideDimensions}
                    disabled={disabled}
                  />
                </Grid>
              </FormSection>
            )}

            {rate && rate.rateType === 'quantity' && (
              <FormSection hideDivider>
                <Grid container spacing={mobile ? 2 : 3} classes={{ container: classes.formRow }}>
                  <Grid item xs={6} sm={3}>
                    <KeypadInputField
                      label={'Quantity'}
                      format={'dimension'}
                      fullWidth
                      name={'quantity'}
                      onChange={handleChange}
                      value={areaSubstrate.current.quantity}
                      allowDecimal
                      onKeyPress={handleKeyPress}
                      autoSelect
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              </FormSection>
            )}

            <Grid container spacing={mobile ? 2 : 3} classes={{ container: classes.formRow }}>
              {rate && rate.rateType !== 'none' && (
                <Grid item xs={6} sm={3} classes={{ root: 'rate-field' }}>
                  <OverridableInputField
                    label={`Rate (${rateLabel})`}
                    format={'rate'}
                    name={'rateValue'}
                    fullWidth
                    onChange={handleRateChange}
                    value={areaSubstrate.current.rateValue}
                    helpPrefix={'Default value:'}
                    hideHelp={areaSubstrate.current.rate.custom}
                    resetBlank
                    onKeyPress={handleKeyPress}
                    autoSelect
                    disabled={disabled}
                  />
                </Grid>
              )}
              {rate && rate.rateType !== 'none' && useCoats && (
                <Grid item xs={6} sm={3} classes={{ root: 'coats-field' }}>
                  <CoatsSelector
                    label={objectLabels.application.plural}
                    rateLabel={rateLabel}
                    onChange={handleChangeCoats}
                    value={areaSubstrate.current.coats}
                    showRates={!areaSubstrate.current.rate.custom}
                    possibleValues={areaSubstrate.current.rate.custom ? [1, 2, 3] : areaSubstrate.current.rate.values}
                    disabled={disabled}
                  />
                </Grid>
              )}

              {!hidePrep && (
                <Grid item xs={6} sm={3} classes={{ root: 'prep-field' }}>
                  <KeypadInputField
                    label={`${objectLabels.prep.value} (hr)`}
                    format={'hours'}
                    fullWidth
                    name={'prep'}
                    onChange={handleChange}
                    value={areaSubstrate.current.prep}
                    allowDecimal
                    onKeyPress={handleKeyPress}
                    autoSelect
                    disabled={disabled}
                  />
                </Grid>
              )}

              {!['pricePerAmount', 'price'].includes(rate.calculationType) && (
                <Grid item={true} xs={12} sm={3} md={3} classes={{ root: 'hours-field' }}>
                  <OverridableInputField
                    label={`${objectLabels.labour.value} (hr)`}
                    format={'hours'}
                    name={'hours'}
                    fullWidth
                    onChange={handleHoursChange}
                    value={areaSubstrate.current.hours}
                    resetBlank
                    helpPrefix={'Calculated at:'}
                    onKeyPress={handleKeyPress}
                    autoSelect
                    flipX
                    disabled={disabled}
                  />
                </Grid>
              )}
              {rate.calculationType === 'pricePerAmount' && rate.rateType !== 'quantity' && (
                <Grid item xs={6} sm={3} classes={{ root: 'estimated-hours-field' }}>
                  <OverridableInputField
                    label={`Hours (est)`}
                    format={'hours'}
                    name={'hours'}
                    fullWidth
                    onChange={handleHoursChange}
                    value={areaSubstrate.current.hours}
                    resetBlank
                    helpPrefix={'Estimated at:'}
                    onKeyPress={handleKeyPress}
                    autoSelect
                    flipX
                    disabled={disabled}
                  />
                </Grid>
              )}
            </Grid>

            <Grid container spacing={mobile ? 2 : 3}>
              <Grid item xs={6}>
                <OverridableInputField
                  label={`Calculated Price (${objectLabels.currency.symbol})`}
                  format={'price'}
                  name={'price'}
                  fullWidth
                  onChange={handlePriceChange}
                  value={areaSubstrate.current.price}
                  resetBlank
                  helpPrefix={'Calculated at:'}
                  onKeyPress={handleKeyPress}
                  autoSelect
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={6} style={{ display: 'flex', alignItems: 'flex-start', paddingTop: '32px' }}>
                {!(hidePrice && hidePrep) && (
                  <FormControlLabel
                    label={'Show Formula'}
                    control={<Checkbox onChange={toggleFormula} checked={showFormula} />}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Collapse show={showFormula && !(hidePrice && hidePrep)}>
                  <CalculationHelper
                    quote={quote}
                    options={options}
                    areaKey={areaKey}
                    areaSubstrateKey={areaSubstrateKey}
                    classes={{ root: classes.calculationHelper }}
                  />
                </Collapse>
              </Grid>
            </Grid>
            <FormSection classes={{ root: classnames(classes.formSection, 'materials') }}>
              <Grid item xs={12} className={classes.title}>
                <Typography variant={'h4'}>{multiProductRatesFeature ? 'Products' : 'Product'}</Typography>
              </Grid>
              <AreaSubstrateProductForm
                options={options}
                quoteType={area.quoteType}
                rate={areaSubstrate.current.rate}
                products={areaSubstrate.current.products}
                onChange={handleProductChange}
                onRemoveProduct={handleRemoveProduct}
                quote={quote}
                areaKey={areaKey}
                areaSubstrateKey={areaSubstrateKey}
                measurementLabel={getUnitLabel(rate.rateType, objectLabels.unit.abbreviation)}
                disabled={disabled}
                checkParentHasChanges={checkParentHasChanges}
              />
            </FormSection>
            <div className="notes">
              {(features.areaViewDescription || features.substrateViewDescription) && (
                <FormSection>
                  <Grid item xs={12} className={classes.title}>
                    <Typography variant={'h4'}>Notes</Typography>
                  </Grid>
                  <Grid container spacing={mobile ? 2 : 3} justifyContent="flex-start" alignItems="flex-end">
                    {features.areaViewDescription && (
                      <Grid item xs={12}>
                        <Tooltip
                          classes={{
                            badgeRoot: classes.badgeRoot,
                            icon: classes.tooltipIcon,
                            badge: classes.tooltipBadge
                          }}
                          title={'Area View Description'}
                          content={`Added to the item description on the Area View. Visible to Customers`}
                        >
                          <Typography className={classes.editorLabel}>{'Area View Description'}</Typography>
                        </Tooltip>
                        <Editor
                          content={areaSubstrate.current.rate.areaReportText}
                          ref={areaViewDescriptionRef}
                          disabled={disabled}
                          onChange={() => {
                            if (!dirtyAreaDescription.current) {
                              dirtyAreaDescription.current = true
                            }
                            handleTextChange()
                          }}
                          fullWidth
                        />
                      </Grid>
                    )}
                    {features.substrateViewDescription && (
                      <Grid item xs={12}>
                        <Tooltip
                          classes={{
                            badgeRoot: classes.badgeRoot,
                            icon: classes.tooltipIcon,
                            badge: classes.tooltipBadge
                          }}
                          title={`${objectLabels.substrate.value} View Description`}
                          content={`Added to the item description on the ${objectLabels.substrate.value} View. Visible to Customers`}
                        >
                          <Typography
                            className={classes.editorLabel}
                          >{`${objectLabels.substrate.value} View Description`}</Typography>
                        </Tooltip>
                        <Editor
                          content={areaSubstrate.current.rate.reportText}
                          ref={substrateViewDescriptionRef}
                          disabled={disabled}
                          onChange={() => {
                            if (!dirtySubstrateDescription.current) {
                              dirtySubstrateDescription.current = true
                            }
                            handleTextChange()
                          }}
                          fullWidth
                        />
                      </Grid>
                    )}
                  </Grid>
                </FormSection>
              )}
              {features.crewNote && (
                <FormSection>
                  <Tooltip
                    classes={{ badgeRoot: classes.badgeRoot, ...tooltipClasses }}
                    title={'Notes'}
                    content={'This note will appear on work orders sent to staff'}
                  >
                    <Typography className={classes.editorLabel}>Crew Note</Typography>
                  </Tooltip>
                  <Editor
                    content={initialCrewNote}
                    ref={crewNoteRef}
                    disabled={disabled && (!isNoteEditable || quote.smartTemplate)}
                    fullWidth
                    onChange={() => {
                      if (!dirtyCrewNote.current) {
                        dirtyCrewNote.current = true
                      }
                      handleTextChange()
                    }}
                  />
                </FormSection>
              )}
            </div>
            {advancedOptionsFeature.enabled && (
              <FormSection classes={{ root: classnames(classes.formSection, 'advanced') }}>
                <FormSectionTitle title={'Advanced Options'} variant={'h4'} />
                <Grid container alignItems={'flex-start'}>
                  {advancedOptionsFeature.workOrderOnly && (
                    <Grid item xs={12}>
                      <Tooltip
                        classes={tooltipClasses}
                        title={'Hide From Customer'}
                        content={`Don't show this ${objectLabels.substrate.value} on the ${objectLabels.quote.value} items.`}
                        iconOnly
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={areaSubstrate.current.workOrderOnly}
                              onChange={handleCheckboxChange('workOrderOnly')}
                              disabled={disabled}
                            />
                          }
                          label={`Hide From Customer`}
                        />
                      </Tooltip>
                    </Grid>
                  )}
                  {advancedOptionsFeature.hideQuantity && rate.rateType === 'quantity' && (
                    <Grid item xs={12}>
                      <Tooltip
                        classes={tooltipClasses}
                        title={'Hide Quantity From Customer'}
                        content={`Hide the quantity of this ${objectLabels.substrate.value} on ${objectLabels.quote.value} items.`}
                        iconOnly
                      >
                        <FormControlLabel
                          label={'Hide Quantity From Customer'}
                          control={
                            <Checkbox
                              checked={areaSubstrate.current.hideQuantity}
                              onChange={handleCheckboxChange('hideQuantity')}
                              disabled={disabled}
                            />
                          }
                        />
                      </Tooltip>
                    </Grid>
                  )}
                  {advancedOptionsFeature.hideQuantityOnWorkOrder && rate.rateType === 'quantity' && (
                    <Grid item xs={12}>
                      <Tooltip
                        classes={tooltipClasses}
                        title={'Hide Quantity From Crew'}
                        content={`Hide the quantity of this ${objectLabels.substrate.value} on ${objectLabels.workOrder.value} items.`}
                        iconOnly
                      >
                        <FormControlLabel
                          label={'Hide Quantity From Crew'}
                          control={
                            <Checkbox
                              checked={areaSubstrate.current.hideQuantityOnWorkOrder}
                              onChange={handleCheckboxChange('hideQuantityOnWorkOrder')}
                              disabled={disabled}
                            />
                          }
                        />
                      </Tooltip>
                    </Grid>
                  )}
                  {advancedOptionsFeature.showProduct && (
                    <Grid item xs={12}>
                      <Tooltip
                        classes={tooltipClasses}
                        title={'Show Product'}
                        content={`Show the product associated with this ${objectLabels.substrate.value} on the ${objectLabels.quote.value}`}
                        iconOnly
                      >
                        <FormControlLabel
                          label={'Show Product'}
                          control={
                            <Checkbox
                              checked={areaSubstrate.current.showProduct}
                              onChange={handleCheckboxChange('showProduct')}
                              disabled={disabled}
                            />
                          }
                        />
                      </Tooltip>
                    </Grid>
                  )}
                  {advancedOptionsFeature.showCoats && (
                    <Grid item xs={12}>
                      <Tooltip
                        classes={tooltipClasses}
                        title={'Show Coats'}
                        content={`Show the coats associated with this ${objectLabels.substrate.value} on the ${objectLabels.quote.value}`}
                        iconOnly
                      >
                        <FormControlLabel
                          label={'Show Coats'}
                          control={
                            <Checkbox
                              checked={areaSubstrate.current.showCoats}
                              onChange={handleCheckboxChange('showCoats')}
                              disabled={disabled}
                            />
                          }
                        />
                      </Tooltip>
                    </Grid>
                  )}
                  {advancedOptionsFeature.showPrep && (
                    <Grid item xs={12}>
                      <Tooltip
                        classes={tooltipClasses}
                        title={'Show Prep Hours'}
                        content={`Show the prep hours associated with this ${objectLabels.substrate.value} on the ${objectLabels.quote.value}`}
                        iconOnly
                      >
                        <FormControlLabel
                          label={'Show Prep Hours'}
                          control={
                            <Checkbox
                              checked={areaSubstrate.current.showPrep}
                              onChange={handleCheckboxChange('showPrep')}
                              disabled={disabled}
                            />
                          }
                        />
                      </Tooltip>
                    </Grid>
                  )}
                  {advancedOptionsFeature.showPrice && (
                    <Grid item xs={12}>
                      <Tooltip
                        classes={tooltipClasses}
                        title={'Show Price'}
                        content={`Show the price associated with this ${objectLabels.substrate.value} on the ${objectLabels.quote.value}`}
                        iconOnly
                      >
                        <FormControlLabel
                          label={'Show Price'}
                          control={
                            <Checkbox
                              onChange={handleCheckboxChange('showPrice')}
                              checked={areaSubstrate.current.showPrice}
                              disabled={disabled}
                            />
                          }
                        />
                      </Tooltip>
                    </Grid>
                  )}
                  {advancedOptionsFeature.showDimensions && rate.rateType.includes('ft') && (
                    <Grid item xs={12}>
                      <Tooltip
                        classes={tooltipClasses}
                        title={'Show Dimensions'}
                        content={`Show the sqft or lnft associated with this ${objectLabels.substrate.value} on the ${objectLabels.quote.value}`}
                        iconOnly
                      >
                        <FormControlLabel
                          label={'Show Dimensions'}
                          control={
                            <Checkbox
                              checked={areaSubstrate.current.showDimensions}
                              onChange={handleCheckboxChange('showDimensions')}
                              disabled={disabled}
                            />
                          }
                        />
                      </Tooltip>
                    </Grid>
                  )}
                  {advancedOptionsFeature.useInches && (
                    <Grid item xs={12}>
                      <Tooltip
                        classes={tooltipClasses}
                        title={'Use Inches'}
                        content={`Use inches for overridding dimensions for this ${objectLabels.substrate.value}.`}
                        iconOnly
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={areaSubstrate.current.useInches}
                              onChange={handleCheckboxChange('useInches')}
                              disabled={disabled}
                            />
                          }
                          label={`Use Inches`}
                        />
                      </Tooltip>
                    </Grid>
                  )}
                  {/* {advancedOptionsFeature.excludeTax && (
                    <Grid item xs={12}>
                      <Tooltip
                        title={'Exclude from Tax'}
                        content={`Don't include this ${objectLabels.substrate.value} when calculating taxes.`}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={areaSubstrate.current.excludeTax}
                              onChange={handleCheckboxChange('excludeTax')}
                              disabled={disabled}
                            />
                          }
                          label={`Exclude from Tax`}
                        />
                      </Tooltip>
                    </Grid>
                  )} */}
                  {/* {advancedOptionsFeature.excludeDiscount && (
                    <Grid item xs={12}>
                      <Tooltip
                        title={'Exclude from Discount'}
                        content={`Don't include this area when calculating discounts.`}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={areaSubstrate.current.excludeDiscount}
                              onChange={handleCheckboxChange('excludeDiscount')}
                              disabled={disabled}
                            />
                          }
                          label={`Exclude from Discount`}
                        />
                      </Tooltip>
                    </Grid>
                  )} */}
                  {advancedOptionsFeature.hourlyRate && (
                    <Grid item xs={12}>
                      <Grid item sm={5}>
                        <Tooltip
                          title={'Custom Hourly Rate'}
                          content={`Override the ${objectLabels.quote.value} Hourly Rate for this ${objectLabels.substrate.value} only.`}
                          iconOnly
                          classes={tooltipClasses}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={areaSubstrate.current.overrideHourlyRate}
                                onChange={handleCheckboxChange('overrideHourlyRate')}
                                disabled={disabled}
                              />
                            }
                            label={`Use Custom Hourly Rate`}
                          />
                        </Tooltip>
                      </Grid>
                      <Grid item sm={4}>
                        {areaSubstrate.current.overrideHourlyRate && (
                          <InputField
                            value={areaSubstrate.current.hourlyRate}
                            onChange={handleChange}
                            name={'hourlyRate'}
                            fullWidth
                            format={'price'}
                            label={'Custom Hourly Rate'}
                            disabled={disabled}
                          />
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </FormSection>
            )}
          </div>
        </form>
      </DialogContent>
      <DialogToggles toggles={toggles} scroll={true} />
      <DialogActions
        leftButton={
          <Button onClick={handleCancel} variant="textNoPadding" className={classes.backButton}>
            Back
          </Button>
        }
        arrows={
          <DialogActionArrows
            hidden={!previousItemKey && !nextItemKey}
            onPrevious={previousItemKey ? () => handleNavArrow(previousItemKey) : null}
            onNext={nextItemKey ? () => handleNavArrow(nextItemKey) : null}
          />
        }
      >
        <Button
          type="button"
          form="edit-rate-form"
          onClick={(e) => {
            console.log('click', e)
            e.preventDefault()
            onComplete()
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </>
  )

  function handleCancel() {
    const updatedAreaSubstrate = syncedAreaSubstrate()
    if (props.onChange) {
      props.onChange(areaKey, updatedAreaSubstrate)
    }
    onCancel()
  }

  function handleNavArrow(itemKey: string) {
    const updatedAreaSubstrate = syncedAreaSubstrate()
    if (props.onChange) {
      props.onChange(areaKey, updatedAreaSubstrate)
    }
    handleNavArrowClick(itemKey)
  }

  function getCurrentAreaSubstrate() {
    return editingKey ? areaSubstrate.current : createAreaSubstrate({ quote, options })
  }

  function onComplete() {
    onConfirm?.(getUpdatedQuote())
  }

  function getUpdatedQuote() {
    const updatedAreaSubstrate = syncedAreaSubstrate()
    const currentAreaSubstrate = getCurrentAreaSubstrate()
    const isAreaSubstrateEqual = isLooselyEqual(currentAreaSubstrate, updatedAreaSubstrate)

    if (isAreaSubstrateEqual) {
      return quote
    } else {
      return updateAreaSubstrate({
        quote,
        options,
        areaKey,
        areaSubstrate: updatedAreaSubstrate,
        calculate: true
      })
    }
  }

  function syncedAreaSubstrate() {
    const areaViewDescription = areaViewDescriptionRef.current?.getHTML()
    const substrateViewDescription = substrateViewDescriptionRef.current?.getHTML()
    const crewNote = crewNoteRef.current?.getHTML()
    const internalLabel = internalLabelRef.current?.value
    const clientLabel = clientLabelRef.current?.value
    const currentAreaSubstrate = getCurrentAreaSubstrate()

    const updatedAreaSubstrate = {
      ...currentAreaSubstrate,
      rate: {
        ...currentAreaSubstrate.rate,
        reportText: dirtySubstrateDescription.current ? substrateViewDescription : currentAreaSubstrate.rate.reportText,
        areaReportText: dirtyAreaDescription.current ? areaViewDescription : currentAreaSubstrate.rate.areaReportText
      },
      notes: dirtyCrewNote.current ? crewNote : currentAreaSubstrate.notes,
      name: internalLabel ?? currentAreaSubstrate.name,
      clientLabel: clientLabel ?? currentAreaSubstrate.clientLabel
    }

    return updatedAreaSubstrate
  }

  function handleTextChange() {
    const updatedAreaSubstrate = syncedAreaSubstrate()

    if (props.onChange) {
      props.onChange(areaKey, updatedAreaSubstrate)
    }
  }

  function handleChange(event: any) {
    let { value } = event.target
    const { name, type } = event.target
    if (value === '') {
      value = '0'
    }
    const currentAreaSubstrate = getCurrentAreaSubstrate()

    const updatedValue = type === 'number' ? parseFloat(value) : value

    const updatedAreaSubstrate = set(currentAreaSubstrate, name, updatedValue)

    if (props.onChange) {
      props.onChange(areaKey, updatedAreaSubstrate)
    }
  }

  function handleCheckboxChange(name: string) {
    return function (ev: any, value: boolean) {
      const currentAreaSubstrate = getCurrentAreaSubstrate()

      const updatedAreaSubstrate = {
        ...currentAreaSubstrate,
        [name]: value
      }

      if (props.onChange) {
        props.onChange(areaKey, updatedAreaSubstrate)
      }
    }
  }

  function handleOverrideDimensions(name: string) {
    return function (value: any) {
      const currentAreaSubstrate = getCurrentAreaSubstrate()
      const updatedAreaSubstrate = set(currentAreaSubstrate, name, value)

      if (props.onChange) {
        props.onChange(areaKey, updatedAreaSubstrate)
      }
    }
  }

  function handleHoursChange(value: OverridableValue) {
    const currentAreaSubstrate = getCurrentAreaSubstrate()

    const updatedAreaSubstrate = set(currentAreaSubstrate, 'hours', value)

    if (props.onChange) {
      props.onChange(areaKey, updatedAreaSubstrate)
    }
  }

  function handlePriceChange(value: OverridableValue) {
    const currentAreaSubstrate = getCurrentAreaSubstrate()

    const updatedAreaSubstrate = set(currentAreaSubstrate, 'price', value)

    if (props.onChange) {
      props.onChange(areaKey, updatedAreaSubstrate)
    }
  }

  function handleRateChange(value: OverridableValue) {
    const currentAreaSubstrate = getCurrentAreaSubstrate()

    const rateValue = {
      ...value,
      custom: parseFloat(value.custom)
    }
    const updatedAreaSubstrate = set(currentAreaSubstrate, 'rateValue', rateValue)
    areaSubstrate.current = updatedAreaSubstrate

    if (props.onChange) {
      props.onChange(areaKey, updatedAreaSubstrate)
    }
  }

  function handleRateTypeChange(rateType: string, calculationType: string) {
    const currentAreaSubstratebstrate = getCurrentAreaSubstrate()

    const quantity = rateType === 'quantity' ? currentAreaSubstratebstrate.quantity : 1

    const updatedAreaSubstrate = {
      ...currentAreaSubstratebstrate,
      quantity,
      rate: {
        ...currentAreaSubstratebstrate.rate,
        rateType,
        calculationType
      }
    }

    if (props.onChange) {
      props.onChange(areaKey, updatedAreaSubstrate)
    }
  }

  function handleProductChange(index: number, product: UpdateableProduct, custom?: boolean) {
    const currentAreaSubstrate = getCurrentAreaSubstrate()

    // If this person selects "custom", we want to just "convert" their
    // existing product into a custom product.
    const newProduct = custom
      ? {
          ...product,
          // ...areaSubstrate.product,
          key: product.key,
          custom: true
        }
      : product

    const updatedProducts = [...currentAreaSubstrate.products]
    if (index === -1) {
      updatedProducts.push(newProduct)
    } else {
      updatedProducts.splice(index, 1, newProduct)
    }

    const updatedAreaSubstrate = {
      ...currentAreaSubstrate,
      products: updatedProducts
    }

    if (props.onChange) {
      props.onChange(areaKey, updatedAreaSubstrate)
    }
  }

  function handleRemoveProduct(index: number) {
    const currentAreaSubstrate = getCurrentAreaSubstrate()
    const updatedAreaSubstrate = {
      ...currentAreaSubstrate,
      products: currentAreaSubstrate.products.filter((_, i) => i !== index)
    }

    if (props.onChange) {
      props.onChange(areaKey, updatedAreaSubstrate)
    }
  }

  function handleChangeCoats(value: number) {
    const currentAreaSubstrate = getCurrentAreaSubstrate()

    const updatedAreaSubstrate = set(currentAreaSubstrate, 'coats', value)
    updatedAreaSubstrate.rateValue = {
      ...updatedAreaSubstrate.rateValue,
      default: currentAreaSubstrate.rate.values[value - 1]
    }

    if (props.onChange) {
      props.onChange(areaKey, updatedAreaSubstrate)
    }
  }

  function handleKeyPress(event: any) {
    if (event.key === 'Enter') {
      onComplete()
    }
  }

  function toggleFormula(_event: React.ChangeEvent<HTMLElement>) {
    const updatedShowFormula = !showFormula
    window.localStorage.setItem('showAreaSubstrateFormula', JSON.stringify(updatedShowFormula))
    setShowFormula(updatedShowFormula)
  }

  function getUnitLabel(rateType: string, unit: UnitAbbreviation): string {
    switch (rateType) {
      case 'sqftWalls':
      case 'sqftCeiling':
      case 'sqftFloor':
        return unit.square
      case 'lnft':
        return unit.linear
      case 'cuft':
        return `cubic ${unit.basic}`
      case 'quantity':
        return 'items'
      default:
        return ''
    }
  }

  function getRateString(
    areaSubstrate: UpdateableAreaSubstrate,
    currencySymbol: string,
    unit: UnitAbbreviation
  ): string {
    const { rate } = areaSubstrate
    const unitLabel = getUnitLabel(rate.rateType, unit)

    if (rate.calculationType === 'amountPerHour') {
      if (rate.rateType === 'quantity') {
        return `hrs/item`
      } else {
        return `${unitLabel}/hr`
      }
    } else if (rate.calculationType === 'hoursPerAmount') {
      if (rate.rateType === 'quantity') {
        return `hrs/item`
      } else {
        return `hrs/${unitLabel}`
      }
    } else if (rate.calculationType === 'pricePerAmount') {
      return `${currencySymbol}/${unitLabel}`
    }

    return 'hrs'
  }
}

export default withStyles(styles)(withDialogStackContext(EditRateForm))
